import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ChatInterface.css";
import { apiBaseUrl } from "../config/environment";
import { useChat } from "../context/ChatContext";
import { callMall, openWhatsApp } from "../utils/callUtils";
import { useLoginOverlay } from "../context/LoginOverlayContext";
import Login from "../pages/Login";
import {
  PRESET_MESSAGES_ROW1,
  PRESET_MESSAGES_ROW2,
} from "../constants/presetMessages";

// Paths extracted from /public svg files — fill="currentColor" inherits CSS color
const CallIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M 101.5,50.5 C 106.667,50.09 111.667,50.7567 116.5,52.5C 133.8,72.4248 150.633,92.7582 167,113.5C 170.282,120.527 170.282,127.527 167,134.5C 158.288,142.052 149.454,149.385 140.5,156.5C 162.891,199.558 195.558,232.225 238.5,254.5C 244.712,244.288 252.045,234.954 260.5,226.5C 265.647,224.501 270.981,224.001 276.5,225C 298.421,241.457 319.755,258.624 340.5,276.5C 343.974,283.04 344.308,289.707 341.5,296.5C 334.012,307.466 326.512,318.466 319,329.5C 298.533,349.129 274.7,354.629 247.5,346C 160.87,311.376 97.3703,251.876 57,167.5C 53.5198,158.718 49.8531,150.051 46,141.5C 39.7147,115.499 46.2147,93.3326 65.5,75C 77.4634,66.5385 89.4634,58.3718 101.5,50.5 Z"
    />
    <path
      fill="currentColor"
      d="M 309.5,59.5 C 319.588,59.2041 325.255,64.0374 326.5,74C 326.053,76.9559 325.22,79.7892 324,82.5C 305.14,101.36 286.306,120.194 267.5,139C 279.833,139.333 292.167,139.667 304.5,140C 317.13,146.831 318.797,155.665 309.5,166.5C 308.088,167.806 306.421,168.64 304.5,169C 279.167,169.667 253.833,169.667 228.5,169C 223.427,167.594 219.927,164.428 218,159.5C 217.026,132.105 217.359,104.771 219,77.5C 226.418,69.1386 234.418,68.472 243,75.5C 244.399,78.3634 245.399,81.3634 246,84.5C 246.623,96.5784 246.623,108.578 246,120.5C 264.389,101.109 283.223,81.9421 302.5,63C 304.925,61.7874 307.259,60.6207 309.5,59.5 Z"
    />
  </svg>
);

const WhatsAppIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      fill="currentColor"
      d="M 186.5,32.5 C 258.117,28.3889 312.951,56.3889 351,116.5C 372.521,155.631 378.188,196.964 368,240.5C 351.095,299.072 313.928,338.572 256.5,359C 205.511,373.961 157.511,367.461 112.5,339.5C 91.2579,346.477 69.9246,353.144 48.5,359.5C 54.858,338.925 61.5247,318.425 68.5,298C 34.2288,246.755 28.0621,192.255 50,134.5C 77.4662,76.3518 122.966,42.3518 186.5,32.5 Z M 138.5,109.5 C 141.85,109.335 145.183,109.502 148.5,110C 151.007,110.093 153.174,110.926 155,112.5C 162.058,127.948 168.558,143.614 174.5,159.5C 170.637,166.553 165.803,172.886 160,178.5C 158.455,180.209 157.622,182.209 157.5,184.5C 172.28,213.775 194.947,234.608 225.5,247C 229.295,247.837 232.961,247.504 236.5,246C 242.646,239.366 248.313,232.366 253.5,225C 255.034,224.077 256.701,223.577 258.5,223.5C 273.956,229.226 288.956,236.059 303.5,244C 307.359,264.939 299.359,279.606 279.5,288C 267.743,293.084 255.743,293.751 243.5,290C 198.336,278.176 162.169,253.342 135,215.5C 125.284,203.408 117.617,190.075 112,175.5C 104.649,152.579 109.149,132.412 125.5,115C 129.474,112.005 133.807,110.171 138.5,109.5 Z"
    />
  </svg>
);

const TalkIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M 325.5,49.5 C 340.925,64.1425 352.759,81.4758 361,101.5C 380.127,153.051 374.127,201.385 343,246.5C 338.19,253.11 332.69,259.11 326.5,264.5C 317.638,256.472 309.305,247.972 301.5,239C 335.763,196.487 341.596,150.32 319,100.5C 313.933,91.4401 308.099,82.9401 301.5,75C 309.664,66.6688 317.664,58.1688 325.5,49.5 Z"
    />
    <path
      fill="currentColor"
      d="M 148.5,95.5 C 175.509,93.7547 196.342,104.088 211,126.5C 225.067,156.828 219.901,183.328 195.5,206C 169.252,224.202 143.252,223.868 117.5,205C 96.6661,185.819 90.4994,162.652 99,135.5C 108.585,113.397 125.085,100.063 148.5,95.5 Z"
    />
    <path
      fill="currentColor"
      d="M 274.5,101.5 C 278.434,103.598 281.6,106.598 284,110.5C 303.46,139.901 304.46,169.901 287,200.5C 283.627,205.25 279.794,209.583 275.5,213.5C 266.992,204.493 258.326,195.659 249.5,187C 262.793,167.216 262.793,147.382 249.5,127.5C 257.855,118.813 266.188,110.146 274.5,101.5 Z"
    />
    <path
      fill="currentColor"
      d="M 142.5,248.5 C 182.983,246.955 220.983,255.789 256.5,275C 265.766,280.932 272.932,288.765 278,298.5C 278.988,301.424 279.654,304.424 280,307.5C 280.5,318.828 280.667,330.162 280.5,341.5C 198.5,341.5 116.5,341.5 34.5,341.5C 26.4276,310.809 35.761,287.643 62.5,272C 87.9121,259.485 114.579,251.652 142.5,248.5 Z"
    />
  </svg>
);

const ChatIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 400 400"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      fill="currentColor"
      d="M 188.5,33.5 C 258.214,29.493 312.047,56.493 350,114.5C 376.265,162.576 379.599,212.242 360,263.5C 333.483,321.331 288.65,354.831 225.5,364C 166.167,364.667 106.833,364.667 47.5,364C 45,362.833 43.1667,361 42,358.5C 41.3333,299.167 41.3333,239.833 42,180.5C 51.1692,117.35 84.6692,72.5167 142.5,46C 157.452,39.9313 172.786,35.7646 188.5,33.5 Z M 130.5,158.5 C 181.501,158.333 232.501,158.5 283.5,159C 291.5,165 291.5,171 283.5,177C 232.5,177.667 181.5,177.667 130.5,177C 122.736,170.896 122.736,164.729 130.5,158.5 Z M 130.5,220.5 C 160.835,220.333 191.168,220.5 221.5,221C 229.5,227 229.5,233 221.5,239C 191.167,239.667 160.833,239.667 130.5,239C 122.736,232.896 122.736,226.729 130.5,220.5 Z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 11H6C4.89543 11 4 11.8954 4 13V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V13C20 11.8954 19.1046 11 18 11Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 15L12 9L6 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 10V12C19 15.87 15.87 19 12 19M5 10V12C5 15.87 8.13 19 12 19M12 19V23M8 23H16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VOICE_ACTIONS = [
  // The chevron occupies one vertical slot in the rail, so action centers
  // are offset by one step from the original values.
  { id: "lock", label: "Continue recording...", icon: LockIcon, y: 112 },
  { id: "talk", label: "Talk now", icon: TalkIcon, y: 168 },
  { id: "call", label: "Get on a call", icon: CallIcon, y: 224 },
  { id: "wa", label: "Switch to WhatsApp", icon: WhatsAppIcon, y: 280 },
];

const GESTURE_THRESHOLDS = {
  deadZone: 20,
  intentLock: 40,
  enterSelection: 70,
  stickySnap: 35,
  cancelLeft: 60,
};
const VOICE_ACTION_TOOLTIP_DISMISSED_KEY = "voiceActionTooltipDismissed";

const ChatInterface = () => {
  const isMallOfFutureDomain =
    typeof window !== "undefined" &&
    window.location.hostname === "mall.looc.ai";
  const mallLabel = isMallOfFutureDomain ? "Mall of The Future" : "Dubai Mall";
  const defaultWhatsAppHelpText = `Hey ${mallLabel}, I need help with...`;

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingTextMessage, setIsSendingTextMessage] = useState(false);
  const [response, setResponse] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // Add message history state
  const [messageHistory, setMessageHistory] = useState([]);
  const [isChatBarVisible] = useState(true);
  const [isChatBarDisabled, setIsChatBarDisabled] = useState(false);
  const [voiceGesture, setVoiceGesture] = useState({
    mode: "idle", // idle | recording | selection | cancel
    intent: null, // null | vertical | horizontal
    selectedAction: null,
    dragUp: 0,
    dragX: 0,
  });
  const [isVoiceGestureActive, setIsVoiceGestureActive] = useState(false);
  const [isMicRecording, setIsMicRecording] = useState(false);
  const [isLockedVoiceRecording, setIsLockedVoiceRecording] = useState(false);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [micError, setMicError] = useState("");
  const [showVoiceActionTooltip, setShowVoiceActionTooltip] = useState(() => {
    if (typeof window === "undefined") return true;
    return (
      window.localStorage.getItem(VOICE_ACTION_TOOLTIP_DISMISSED_KEY) !== "true"
    );
  });
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [micPermissionState, setMicPermissionState] = useState("unknown");
  const micPermissionModalOpenedAtRef = useRef(0);
  const voiceGestureRef = useRef(voiceGesture);
  const moveGestureRef = useRef(() => {});
  const endGestureRef = useRef(() => {});
  const handleActionClickRef = useRef(null);
  const handleSendVoiceRef = useRef(null);
  const stopMicCaptureRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const beepAudioContextRef = useRef(null);
  const recordingStartMsRef = useRef(0);
  const gestureStartRef = useRef({ x: 0, y: 0, pointerId: null });
  const suppressLockedSendClickRef = useRef(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const thinkingDotsTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    focusChatInputRef,
    blurChatInputRef,
    setIsFocused: setContextFocused,
  } = useChat();
  const { showLoginSheet, setShowLoginSheet } = useLoginOverlay();

  // Remove auto-focus - let user explicitly focus the input
  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, []);

  // Add effect to manage app opacity when input is focused instead of response visible
  useEffect(() => {
    if (isFocused) {
      document.body.classList.add("chat-response-active");
    } else {
      document.body.classList.remove("chat-response-active");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("chat-response-active");
    };
  }, [isFocused]);

  // Add effect to manage focus state classes
  useEffect(() => {
    if (isFocused) {
      document.body.classList.add("chat-input-focused");
    } else {
      document.body.classList.remove("chat-input-focused");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("chat-input-focused");
    };
  }, [isFocused]);

  // Add effect to re-enable chat bar whenever input mode is exited
  useEffect(() => {
    if (!isFocused) {
      setIsChatBarDisabled(false);
    }
  }, [isFocused]);

  useEffect(() => {
    if (location.state?.resetMicTooltip) {
      setShowVoiceActionTooltip(true);

      const nextState = { ...location.state };
      delete nextState.resetMicTooltip;
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      });
    }
  }, [location, navigate]);

  // Expose focus function to context
  useEffect(() => {
    focusChatInputRef.current = () => {
      if (inputRef.current && !isChatBarDisabled) {
        inputRef.current.focus();
        setIsFocused(true);
      }
    };
    blurChatInputRef.current = () => {
      if (inputRef.current) {
        inputRef.current.blur();
        setIsFocused(false);
        setIsChatBarDisabled(false);
      }
    };
  }, [focusChatInputRef, blurChatInputRef, isChatBarDisabled]);

  // Sync focus state with context
  useEffect(() => {
    setContextFocused(isFocused);
  }, [isFocused, setContextFocused]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current && messagesScrollRef.current) {
      messagesScrollRef.current.scrollTop =
        messagesScrollRef.current.scrollHeight;
    }
  }, [messageHistory, response]);

  // Auto-navigate to Explore tab when reply has "Live talk" URL
  useEffect(() => {
    if (response?.url?.url && response?.url?.display_text === "Live talk") {
      // Navigate to Explore tab with URL in state
      navigate("/explore", { state: { url: response.url.url } });
      // Exit chat mode
      setIsVisible(false);
      setResponse(null);
      setIsFocused(false);
      setIsChatBarDisabled(false);
      setMessageHistory([]);
    }
  }, [response, navigate]);

  // Function to decode JWT token and extract tenant from Alias claim
  const getTenantFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.Alias;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Function to play beep sound
  const playBeep = () => {
    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return;

      if (!beepAudioContextRef.current) {
        beepAudioContextRef.current = new AudioContextCtor();
      }

      const audioContext = beepAudioContextRef.current;
      const startTone = () => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800; // Beep frequency in Hz
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.1,
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      };

      if (audioContext.state === "suspended") {
        audioContext
          .resume()
          .then(startTone)
          .catch((error) => {
            console.error("Error resuming beep audio context:", error);
          });
      } else {
        startTone();
      }
    } catch (error) {
      console.error("Error playing beep sound:", error);
    }
  };

  const formatRecordingTimer = (elapsedMs) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  // Function to convert basic Markdown to HTML
  const formatMarkdown = (text) => {
    if (!text) return "";

    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // **bold**
      .replace(/\*(.*?)\*/g, "<em>$1</em>") // *italic*
      .replace(/`(.*?)`/g, "<code>$1</code>") // `code`
      .replace(/\n/g, "<br>"); // line breaks
  };

  const convertBlobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Unable to convert audio blob to base64."));
          return;
        }
        const [, base64 = ""] = reader.result.split(",");
        if (!base64) {
          reject(new Error("Empty base64 audio payload."));
          return;
        }
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed reading audio blob."));
      reader.readAsDataURL(blob);
    });

  const clearThinkingDotsTimer = () => {
    if (thinkingDotsTimerRef.current) {
      clearTimeout(thinkingDotsTimerRef.current);
      thinkingDotsTimerRef.current = null;
    }
  };

  const addDelayedPlaceholder = () => {
    clearThinkingDotsTimer();
    thinkingDotsTimerRef.current = setTimeout(() => {
      setMessageHistory((prevHistory) => {
        if (
          prevHistory.length > 0 &&
          prevHistory[prevHistory.length - 1] === "A:..."
        ) {
          return prevHistory;
        }
        return [...prevHistory, "A:..."];
      });
      thinkingDotsTimerRef.current = null;
    }, 1000);
  };

  const setFinalAssistantMessage = (text) => {
    clearThinkingDotsTimer();
    const aiMessage = `A:${text}`;
    setMessageHistory((prevHistory) => {
      if (
        prevHistory.length > 0 &&
        prevHistory[prevHistory.length - 1] === "A:..."
      ) {
        return [...prevHistory.slice(0, -1), aiMessage];
      }
      return [...prevHistory, aiMessage];
    });
  };

  useEffect(() => {
    return () => {
      clearThinkingDotsTimer();
    };
  }, []);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    // Store message and clear input immediately
    const messageToSend = message.trim();
    setMessage(""); // Clear input immediately without waiting for response
    setIsLoading(true);
    setIsSendingTextMessage(true);
    setResponse(null);

    // Add user message to history
    const userMessage = `U:${messageToSend}`;
    setMessageHistory((prevHistory) => [...prevHistory, userMessage]);
    addDelayedPlaceholder();

    try {
      // Get token from localStorage
      const token = localStorage.getItem("authToken") || "";

      // Extract tenant from token
      const tenant = getTenantFromToken(token);
      if (!tenant) {
        throw new Error("Failed to extract tenant from token");
      }

      // Call backend API with WhatsApp message format
      const response = await fetch(`https://${tenant}.looc.ai/api/uai/msg`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          type: "text",
          text: {
            body: messageToSend,
          },
        }),
      });

      if (response.status === 401) {
        setShowLoginSheet(true);
        throw new Error("Unauthorized - please login");
      }

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();

      // Check if response is successful (status: 0)
      if (data.status !== 0 || !data.data) {
        throw new Error("Invalid response format");
      }

      const responseData = data.data;
      const aiResponse =
        responseData.msg || "Sorry, I couldn't process your request.";

      // Extract navigation suggestion
      const navigationMatch = aiResponse.match(/\[NAVIGATE:(\w+)\]/);
      const navigationSection = navigationMatch ? navigationMatch[1] : null;

      // Extract connect message
      const connectMatch = aiResponse.match(/\[CONNECT:(.*?)\]/);
      const connectMessage = connectMatch ? connectMatch[1] : null;

      // Clean response by removing both navigation and connect tags
      const cleanResponse = aiResponse
        .replace(/\[NAVIGATE:\w+\]/, "")
        .replace(/\[CONNECT:.*?\]/, "")
        .trim();

      // Replace delayed placeholder (if shown) or append response directly
      setFinalAssistantMessage(cleanResponse);

      setResponse({
        text: cleanResponse,
        header: responseData.header,
        footer: responseData.footer,
        img: responseData.img,
        actionsTitle: responseData.actionsTitle,
        cards: responseData.cards,
        actions: responseData.actions,
        type: responseData.type,
        voice: responseData.voice,
        url: responseData.url,
        location: responseData.location,
        navigationSection: navigationSection,
        connectMessage: connectMessage,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling chat API:", error);
      const errorResponse =
        "Sorry, your session expired! Please just retry now";

      // Replace delayed placeholder (if shown) or append error directly
      setFinalAssistantMessage(errorResponse);

      setResponse({
        text: errorResponse,
        navigationSection: null,
        connectMessage: null,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on error reply

      // Don't clear input on error - user can retry with same message
    } finally {
      clearThinkingDotsTimer();
      setIsLoading(false);
      setIsSendingTextMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = async (cmd) => {
    if (!cmd || isLoading) return;

    setIsLoading(true);
    setResponse(null);

    // Add user message to history
    const userMessage = `U:${cmd}`;
    setMessageHistory((prevHistory) => [...prevHistory, userMessage]);
    addDelayedPlaceholder();

    try {
      // Get token from localStorage
      const token = localStorage.getItem("authToken") || "";

      // Extract tenant from token
      const tenant = getTenantFromToken(token);
      if (!tenant) {
        throw new Error("Failed to extract tenant from token");
      }

      // Call backend API with WhatsApp message format
      const response = await fetch(`https://${tenant}.looc.ai/api/uai/msg`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          type: "text",
          text: {
            body: cmd,
          },
        }),
      });

      if (response.status === 401) {
        setShowLoginSheet(true);
        throw new Error("Unauthorized - please login");
      }

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();

      // Check if response is successful (status: 0)
      if (data.status !== 0 || !data.data) {
        throw new Error("Invalid response format");
      }

      const responseData = data.data;
      const aiResponse =
        responseData.msg || "Sorry, I couldn't process your request.";

      // Extract navigation suggestion
      const navigationMatch = aiResponse.match(/\[NAVIGATE:(\w+)\]/);
      const navigationSection = navigationMatch ? navigationMatch[1] : null;

      // Extract connect message
      const connectMatch = aiResponse.match(/\[CONNECT:(.*?)\]/);
      const connectMessage = connectMatch ? connectMatch[1] : null;

      // Clean response by removing both navigation and connect tags
      const cleanResponse = aiResponse
        .replace(/\[NAVIGATE:\w+\]/, "")
        .replace(/\[CONNECT:.*?\]/, "")
        .trim();

      // Replace delayed placeholder (if shown) or append response directly
      setFinalAssistantMessage(cleanResponse);

      setResponse({
        text: cleanResponse,
        header: responseData.header,
        footer: responseData.footer,
        img: responseData.img,
        actionsTitle: responseData.actionsTitle,
        cards: responseData.cards,
        actions: responseData.actions,
        type: responseData.type,
        voice: responseData.voice,
        url: responseData.url,
        location: responseData.location,
        navigationSection: navigationSection,
        connectMessage: connectMessage,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling chat API:", error);
      const errorResponse =
        "Sorry, your session expired! Please just retry now";

      // Replace delayed placeholder (if shown) or append error directly
      setFinalAssistantMessage(errorResponse);

      setResponse({
        text: errorResponse,
        navigationSection: null,
        connectMessage: null,
      });
      setIsVisible(true);
      playBeep(); // Play beep sound on error reply
    } finally {
      clearThinkingDotsTimer();
      setIsLoading(false);
    }
  };

  handleActionClickRef.current = handleActionClick;

  const handleSendVoice = async (voiceNote) => {
    if (!voiceNote || !voiceNote.blob || isLoading) return;

    setIsLoading(true);
    setResponse(null);

    const durationSeconds = Math.max(
      1,
      Math.round(voiceNote.durationMs / 1000),
    );
    const userMessage = `U_AUDIO:${durationSeconds}`;
    setMessageHistory((prevHistory) => [...prevHistory, userMessage]);
    addDelayedPlaceholder();

    try {
      // Get token from localStorage
      const token = localStorage.getItem("authToken") || "";

      const tenant = getTenantFromToken(token);
      if (!tenant) {
        throw new Error("Failed to extract tenant from token");
      }

      const base64Audio = await convertBlobToBase64(voiceNote.blob);

      const response = await fetch(
        `https://${tenant}.looc.ai/api/uai/voice?getVoice=false`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify({
            type: "audio",
            audio: {
              base64: base64Audio,
            },
          }),
        },
      );

      if (response.status === 401) {
        setShowLoginSheet(true);
        throw new Error("Unauthorized - please login");
      }

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();

      if (data.status !== 0 || !data.data) {
        throw new Error("Invalid response format");
      }

      const responseData = data.data;
      const aiResponse =
        responseData.msg || "Sorry, I couldn't process your request.";

      const navigationMatch = aiResponse.match(/\[NAVIGATE:(\w+)\]/);
      const navigationSection = navigationMatch ? navigationMatch[1] : null;

      const connectMatch = aiResponse.match(/\[CONNECT:(.*?)\]/);
      const connectMessage = connectMatch ? connectMatch[1] : null;

      const cleanResponse = aiResponse
        .replace(/\[NAVIGATE:\w+\]/, "")
        .replace(/\[CONNECT:.*?\]/, "")
        .trim();

      setFinalAssistantMessage(cleanResponse);

      setResponse({
        text: cleanResponse,
        header: responseData.header,
        footer: responseData.footer,
        img: responseData.img,
        actionsTitle: responseData.actionsTitle,
        cards: responseData.cards,
        actions: responseData.actions,
        type: responseData.type,
        voice: responseData.voice,
        url: responseData.url,
        location: responseData.location,
        navigationSection: navigationSection,
        connectMessage: connectMessage,
      });
      setIsVisible(true);
      playBeep();
    } catch (error) {
      console.error("Error calling voice API:", error);
      const errorResponse =
        "Sorry, your session expired! Please just retry now";

      setFinalAssistantMessage(errorResponse);

      setResponse({
        text: errorResponse,
        navigationSection: null,
        connectMessage: null,
      });
      setIsVisible(true);
      playBeep();
    } finally {
      clearThinkingDotsTimer();
      setIsLoading(false);
    }
  };
  handleSendVoiceRef.current = handleSendVoice;

  const handleNavigate = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (response?.navigationSection) {
      const routes = {
        home: "/",
        beneficiaries: "/beneficiaries",
        cards: "/cards",
      };

      const route = routes[response.navigationSection];
      if (route) {
        navigate(route);
        // Don't clear response - keep buttons visible
        setIsFocused(false); // Exit input mode
      }
    }
  };

  const handleConnect = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    setIsLoading(true);

    try {
      // Call the assist API endpoint
      const apiResponse = await fetch(`${apiBaseUrl}/api/ai/assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          msg: response.connectMessage,
        }),
      });

      if (!apiResponse.ok) {
        throw new Error("Failed to get assist response");
      }

      const data = await apiResponse.json();
      const assistResponse =
        data.message ||
        "Thank you, we have created a ticket for you. Click below to interact now with our specialized representative.";

      // Set the response message and show WhatsApp button
      setResponse({
        text: assistResponse,
        navigationSection: null,
        connectMessage: null,
        showWhatsApp: true,
      });
      playBeep(); // Play beep sound on chat reply
    } catch (error) {
      console.error("Error calling assist API:", error);
      // Fallback to default message on error
      setResponse({
        text: "Thank you, we have created a ticket for you. Click below to interact now with our specialized representative.",
        navigationSection: null,
        connectMessage: null,
        showWhatsApp: true,
      });
      playBeep(); // Play beep sound on error reply
    } finally {
      setIsLoading(false);
    }

    setIsVisible(true);
    setIsChatBarDisabled(true); // Disable the chat bar
  };

  const handleWhatsAppClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    openWhatsApp();
    // Don't clear response - keep buttons visible
  };

  const handleUrlClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (response?.url?.url) {
      // Route "Live talk" iframe into Explore tab
      if (response?.url?.display_text === "Live talk") {
        navigate("/explore", { state: { url: response.url.url } });
        setIsVisible(false);
        setResponse(null);
        setIsFocused(false);
        setIsChatBarDisabled(false);
        setMessageHistory([]);
        return;
      }

      // Check if URL starts with map.abuzz.tech
      if (response.url.url.startsWith("https://map.abuzz.tech/")) {
        // Navigate to Navigate tab with URL in state
        navigate("/navigate", { state: { url: response.url.url } });
        // Close the response card and chat messages view
        setIsVisible(false);
        setResponse(null);
        setIsFocused(false);
        setIsChatBarDisabled(false);
        setMessageHistory([]); // Clear message history to close the chat window
      } else {
        // Default behavior: open in new window
        window.open(response.url.url, "_blank");
      }
    }
  };

  const closeResponse = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsVisible(false);
    setResponse(null);
    setIsFocused(false);
    setIsChatBarDisabled(false); // Re-enable chat bar when closing response
    setMessageHistory([]); // Clear message history to close the chat window
  };

  const handleCloseCard = (cardIndex) => (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setResponse((prevResponse) => {
      if (prevResponse?.cards && Array.isArray(prevResponse.cards)) {
        const updatedCards = prevResponse.cards.filter(
          (_, index) => index !== cardIndex,
        );
        return {
          ...prevResponse,
          cards: updatedCards.length > 0 ? updatedCards : null,
        };
      }
      return prevResponse;
    });
  };

  const handleInputFocus = () => {
    dismissVoiceActionTooltip();
    setIsFocused(true);
  };

  const blurInputForMicInteraction = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setIsFocused(false);
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    setIsFocused(false);
    setIsChatBarDisabled(false); // Re-enable chat bar when exiting input mode
    if (isVisible || response) {
      setIsVisible(false);
      setResponse(null);
    }
    // Exit fullscreen chat mode by clearing message history (always clear, even if empty)
    setMessageHistory([]);
  };

  const handleChatAreaClick = (e) => {
    e.stopPropagation();
  };

  const dismissVoiceActionTooltip = () => {
    setShowVoiceActionTooltip(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VOICE_ACTION_TOOLTIP_DISMISSED_KEY, "true");
    }
  };

  // Add specific handlers for buttons to prevent propagation
  const handleButtonClick = (originalHandler) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    originalHandler(e);
  };

  const getNearestAction = (dragUp) => {
    // With column-reverse: visual order is lock(bottom), talk(middle), call(top)
    // Array: [lock(y:56), talk(y:112), call(y:168)]
    // The y values represent drag distances: lock=56 (bottom), talk=112 (middle), call=168 (top)
    return VOICE_ACTIONS.reduce(
      (closest, action) => {
        const distance = Math.abs(dragUp - action.y);
        if (distance < closest.distance) {
          return { id: action.id, distance };
        }
        return closest;
      },
      { id: null, distance: Number.POSITIVE_INFINITY },
    );
  };

  const resetVoiceGesture = () => {
    setIsVoiceGestureActive(false);
    setVoiceGesture({
      mode: "idle",
      intent: null,
      selectedAction: null,
      dragUp: 0,
      dragX: 0,
    });
    gestureStartRef.current = { x: 0, y: 0, pointerId: null };
  };

  const stopActiveMediaStream = () => {
    if (!mediaStreamRef.current) return;
    mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const getMicrophonePermissionState = async () => {
    if (!navigator.permissions || !navigator.permissions.query) {
      return "unknown";
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: "microphone",
      });
      return permissionStatus.state;
    } catch (error) {
      console.error("Failed to query microphone permission:", error);
      return "unknown";
    }
  };

  const openMicPermissionModal = (permissionState = "unknown") => {
    setMicPermissionState(permissionState);
    micPermissionModalOpenedAtRef.current = Date.now();
    setShowMicPermissionModal(true);
  };

  const requestMicrophonePermission = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.error("Failed to request microphone permission:", error);
      return false;
    }
  };

  const startMicCapture = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicError("Microphone is not supported in this browser.");
      return false;
    }

    try {
      setMicError("");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const candidateMimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ];
      const mimeType = candidateMimeTypes.find((type) =>
        window.MediaRecorder?.isTypeSupported?.(type),
      );
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        stopActiveMediaStream();
      };

      mediaRecorderRef.current = recorder;
      recordingStartMsRef.current = Date.now();
      setRecordingElapsedMs(0);
      recorder.start(200);
      setIsMicRecording(true);
      setShowMicPermissionModal(false);
      return true;
    } catch (error) {
      console.error("Failed to start microphone capture:", error);
      setMicError("Microphone permission is required.");
      if (
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError"
      ) {
        openMicPermissionModal("denied");
      }
      stopActiveMediaStream();
      return false;
    }
  };

  const stopMicCapture = async ({ discard = false } = {}) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setIsMicRecording(false);
      return null;
    }

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const durationMs = Date.now() - recordingStartMsRef.current;
        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopActiveMediaStream();
        setIsMicRecording(false);
        setRecordingElapsedMs(0);

        if (discard || blob.size === 0) {
          resolve(null);
          return;
        }

        resolve({
          blob,
          durationMs,
          mimeType,
        });
      };

      if (recorder.state === "inactive") {
        recorder.onstop();
      } else {
        recorder.stop();
      }
    });
  };
  stopMicCaptureRef.current = stopMicCapture;

  const handleVoiceGestureStart = async (e) => {
    if (
      message.trim() ||
      isLoading ||
      isChatBarDisabled ||
      isLockedVoiceRecording
    )
      return;

    e.preventDefault();
    e.stopPropagation();

    // Prevent focus/selection highlight
    if (e.currentTarget) {
      e.currentTarget.blur();
    }

    let effectivePermissionState = micPermissionState;
    if (effectivePermissionState === "unknown") {
      effectivePermissionState = await getMicrophonePermissionState();
      setMicPermissionState(effectivePermissionState);
    }

    if (effectivePermissionState !== "granted") {
      setMicError("Microphone permission is required.");
      openMicPermissionModal(effectivePermissionState);
      const granted = await requestMicrophonePermission();
      const refreshedPermissionState = granted
        ? "granted"
        : await getMicrophonePermissionState();
      setMicPermissionState(refreshedPermissionState);
      return;
    }

    gestureStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      pointerId: e.pointerId,
    };

    // Activate gesture UI immediately so a simple press shows the rail animation.
    setIsVoiceGestureActive(true);
    setVoiceGesture({
      mode: "recording", // Start in recording mode, not selection mode
      intent: null,
      selectedAction: null, // No preselection
      dragUp: 0,
      dragX: 0,
    });

    playBeep();
    const recordingStarted = await startMicCapture();
    if (!recordingStarted) {
      resetVoiceGesture();
      return;
    }

    // Refresh permission state for next attempts.
    getMicrophonePermissionState().then((state) => {
      setMicPermissionState(state);
    });
  };

  useEffect(() => {
    voiceGestureRef.current = voiceGesture;
  }, [voiceGesture]);

  useEffect(() => {
    if (!isMicRecording) return undefined;

    const updateElapsed = () => {
      setRecordingElapsedMs(Date.now() - recordingStartMsRef.current);
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isMicRecording]);

  useEffect(() => {
    moveGestureRef.current = (e) => {
      if (!isVoiceGestureActive || voiceGestureRef.current.mode === "idle")
        return;

      const { x, y } = gestureStartRef.current;
      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;
      const dragUp = Math.max(0, -deltaY);
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      let nextIntent = voiceGestureRef.current.intent;
      let nextMode = voiceGestureRef.current.mode;
      let nextSelectedAction = voiceGestureRef.current.selectedAction;

      if (!nextIntent && Math.max(absX, absY) > GESTURE_THRESHOLDS.intentLock) {
        nextIntent = absY > absX ? "vertical" : "horizontal";
      }

      // Check for cancel:
      // 1) hard left swipe, OR
      // 2) move back down while left, OR
      // 3) hover in the cancel track area near the base before release.
      const isSwipingLeft = deltaX < -GESTURE_THRESHOLDS.cancelLeft;
      const isSwipingBackDown = deltaY > 20; // Positive deltaY means moving down
      const isMovingLeftWhileDown = isSwipingBackDown && deltaX < -20;
      const isHoveringCancelTrack = deltaX < -26 && deltaY > -18;

      if (
        (nextIntent === "horizontal" && isSwipingLeft) ||
        isMovingLeftWhileDown ||
        isHoveringCancelTrack
      ) {
        nextMode = "cancel";
      } else if (nextIntent === "vertical" || nextMode === "selection") {
        if (dragUp > GESTURE_THRESHOLDS.enterSelection) {
          nextMode = "selection";
        } else {
          // Don't enter selection mode until dragUp reaches threshold
          nextMode = "recording";
          nextSelectedAction = null;
        }
      }

      if (nextMode === "selection") {
        // Only select when dragUp is close enough to a button's y value
        const nearest = getNearestAction(dragUp);
        const threshold = 30; // Distance threshold to consider a button "reached"
        if (nearest.distance <= threshold) {
          nextSelectedAction = nearest.id;
        } else {
          nextSelectedAction = null;
        }
      } else {
        nextSelectedAction = null;
      }

      setVoiceGesture((prev) => ({
        ...prev,
        mode: nextMode,
        intent: nextIntent,
        selectedAction: nextSelectedAction,
        dragUp,
        dragX: deltaX,
      }));
    };

    endGestureRef.current = async () => {
      if (!isVoiceGestureActive) return;

      const { mode, selectedAction } = voiceGestureRef.current;
      resetVoiceGesture();

      if (mode === "cancel") {
        await stopMicCaptureRef.current?.({ discard: true });
        return;
      }
      if (mode === "selection" || !!selectedAction) {
        if (selectedAction === "lock") {
          suppressLockedSendClickRef.current = true;
          setIsLockedVoiceRecording(true);
          return;
        }

        // Any interaction with the vertical action rail must never send voice note.
        await stopMicCaptureRef.current?.({ discard: true });

        if (selectedAction === "call") {
          const storedPhoneNumber = (
            localStorage.getItem("phoneNumber") || ""
          ).trim();
          if (storedPhoneNumber.toLowerCase() === "guest") {
            callMall();
            return;
          }
          handleActionClickRef.current?.("Please call me now on the phone");
          return;
        }
        if (selectedAction === "talk") {
          handleActionClickRef.current?.("I want to connect live please");
          return;
        }
        if (selectedAction === "wa") {
          openWhatsApp(defaultWhatsAppHelpText);
          return;
        }

        return;
      }

      // If no action selected (or mode is recording), send voice note
      const voiceNote = await stopMicCaptureRef.current?.({ discard: false });
      if (!voiceNote) return;

      await handleSendVoiceRef.current?.(voiceNote);
    };
  }, [isVoiceGestureActive, defaultWhatsAppHelpText]);

  useEffect(() => {
    if (!isVoiceGestureActive) return undefined;

    const onPointerMove = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      event.preventDefault();
      moveGestureRef.current(event);
    };

    const onPointerUp = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      const { mode, selectedAction } = voiceGestureRef.current;
      if (mode === "cancel" || mode === "selection" || !!selectedAction) {
        endGestureRef.current();
        return;
      }
      // Default lift behavior: keep recording running in locked mode.
      suppressLockedSendClickRef.current = true;
      setIsLockedVoiceRecording(true);
      resetVoiceGesture();
    };

    const onPointerCancel = (event) => {
      if (event.pointerId !== gestureStartRef.current.pointerId) return;
      resetVoiceGesture();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
    window.addEventListener("pointercancel", onPointerCancel, {
      passive: false,
    });

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [isVoiceGestureActive]);

  const cancelProgress = Math.min(
    1,
    Math.max(0, -voiceGesture.dragX / GESTURE_THRESHOLDS.cancelLeft),
  );
  useEffect(() => {
    return () => {
      stopActiveMediaStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
    };
  }, []);

  const handleSendLockedVoice = async () => {
    if (!isLockedVoiceRecording || isLoading) return;

    setIsLockedVoiceRecording(false);
    const voiceNote = await stopMicCaptureRef.current?.({ discard: false });
    if (!voiceNote) return;

    await handleSendVoiceRef.current?.(voiceNote);
  };

  const handleDiscardLockedVoice = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLockedVoiceRecording || isLoading) return;

    setIsLockedVoiceRecording(false);
    resetVoiceGesture();
    await stopMicCaptureRef.current?.({ discard: true });
  };

  const handlePresetClick = (presetText, presetType) => {
    if (isLoading || isChatBarDisabled) return;

    // If type is "wa", open WhatsApp with the text
    if (presetType === "wa") {
      openWhatsApp(presetText);
      return;
    }

    // Append appropriate suffix based on type
    let messageToSend = presetText;
    if (presetType === "call") {
      messageToSend = `${presetText} - Please call me now on the phone`;
    } else if (presetType === "talk") {
      messageToSend = `${presetText} - I want to connect live please`;
    }

    // Send directly via handleActionClick
    handleActionClick(messageToSend);
  };

  const getPresetIcon = (type) => {
    switch (type) {
      case "call":
        return <CallIcon size={16} />;
      case "wa":
        return <WhatsAppIcon size={16} />;
      case "talk":
        return <TalkIcon size={16} />;
      case "chat":
      default:
        return <ChatIcon size={16} />;
    }
  };

  const handleFloatingWhatsAppClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    openWhatsApp(defaultWhatsAppHelpText);
  };

  const closeMicPermissionModal = () => {
    setShowMicPermissionModal(false);
  };

  const handleMicPermissionModalOverlayClick = () => {
    // Ignore the release click from the same touch that opened the modal.
    if (Date.now() - micPermissionModalOpenedAtRef.current < 350) {
      return;
    }
    closeMicPermissionModal();
  };

  return (
    <>
      {/* Login overlay */}
      {showLoginSheet && (
        <Login
          asBottomSheet={true}
          isOpen={showLoginSheet}
          onClose={() => setShowLoginSheet(false)}
        />
      )}

      {showMicPermissionModal && (
        <div
          className="mic-permission-modal-overlay"
          onClick={handleMicPermissionModalOverlayClick}
          role="presentation"
        >
          <div
            className="mic-permission-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mic-permission-modal-title"
          >
            <h3 id="mic-permission-modal-title">Enable microphone access</h3>
            <p>
              {micPermissionState === "denied"
                ? "Microphone access is currently blocked. Please allow microphone permission in your browser/site settings, then try again."
                : "Microphone access is not granted yet. Please allow microphone permission when prompted, then try again."}
            </p>
            <button
              type="button"
              className="mic-permission-modal-button"
              onClick={closeMicPermissionModal}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Overlay OUTSIDE chat-interface so it's actually clickable */}
      {isFocused && (
        <div className="chat-overlay" onClick={handleOverlayClick} />
      )}

      {/* Floating WhatsApp icon - hidden when conversation has messages */}
      {messageHistory.length === 0 && (
        <button
          className="floating-whatsapp-button"
          onClick={handleFloatingWhatsAppClick}
          aria-label="Switch to WhatsApp"
        >
          <img src="/icon-wa.svg" alt="WhatsApp" />
        </button>
      )}

      {/* Preset messages - visible when chat is focused and no messages in history */}
      {isFocused && messageHistory.length === 0 && (
        <div className="preset-messages-wrapper">
          <div className="preset-title">
            HEY! I AM YOUR PERSONAL MALL CONCIERGE ;)
          </div>
          <div className="preset-messages-row">
            <div className="preset-messages-track">
              {[...PRESET_MESSAGES_ROW1, ...PRESET_MESSAGES_ROW1].map(
                (preset, index) => (
                  <button
                    key={`row1-${index}`}
                    className="preset-message-button"
                    onClick={() => handlePresetClick(preset.text, preset.type)}
                    disabled={isLoading || isChatBarDisabled}
                  >
                    <span className="preset-icon">
                      {getPresetIcon(preset.type)}
                    </span>
                    <span className="preset-text">{preset.text}</span>
                  </button>
                ),
              )}
            </div>
          </div>
          <div className="preset-messages-row preset-messages-row-reverse">
            <div className="preset-messages-track">
              {[...PRESET_MESSAGES_ROW2, ...PRESET_MESSAGES_ROW2].map(
                (preset, index) => (
                  <button
                    key={`row2-${index}`}
                    className="preset-message-button"
                    onClick={() => handlePresetClick(preset.text, preset.type)}
                    disabled={isLoading || isChatBarDisabled}
                  >
                    <span className="preset-icon">
                      {getPresetIcon(preset.type)}
                    </span>
                    <span className="preset-text">{preset.text}</span>
                  </button>
                ),
              )}
            </div>
          </div>
        </div>
      )}

      <div className="chat-interface">
        {/* Chat messages area - now inline above input */}
        {messageHistory.length > 0 && (
          <div
            className="chat-messages-container"
            onClick={handleChatAreaClick}
          >
            <button
              className="close-response"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeResponse(e);
              }}
            >
              ×
            </button>
            <div className="chat-messages-scroll" ref={messagesScrollRef}>
              {messageHistory.map((msg, index) => {
                const isVoiceMessage = msg.startsWith("U_AUDIO:");
                const isUser = isVoiceMessage || msg.startsWith("U:");
                const messageText = isVoiceMessage
                  ? msg.substring("U_AUDIO:".length)
                  : msg.substring(2);
                const isPlaceholder = !isUser && messageText === "...";
                const isLastMessage = index === messageHistory.length - 1;
                const showUrlInsideBubble =
                  !isUser &&
                  !isPlaceholder &&
                  isLastMessage &&
                  response?.url?.display_text &&
                  response?.url?.url;
                return (
                  <div
                    key={index}
                    className={`chat-message ${isUser ? "user-message" : "ai-message"}`}
                  >
                    {isVoiceMessage ? (
                      <div className="message-content voice-message">
                        <span
                          className="voice-message-icon"
                          role="img"
                          aria-label={`Voice message ${messageText || ""} seconds`}
                          title={`Voice message ${messageText || ""} seconds`}
                        >
                          <MicIcon />
                        </span>
                      </div>
                    ) : isPlaceholder ? (
                      <div
                        className="message-content placeholder"
                        aria-label="Assistant is typing"
                      >
                        <span className="animated-dot dot-1">.</span>
                        <span className="animated-dot dot-2">.</span>
                        <span className="animated-dot dot-3">.</span>
                      </div>
                    ) : (
                      <div className="message-content">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatMarkdown(messageText),
                          }}
                        />
                        {showUrlInsideBubble && (
                          <div className="message-inline-action">
                            <button
                              className="url-button"
                              onClick={handleButtonClick(handleUrlClick)}
                            >
                              {response.url.display_text}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {response?.img && (
                <div className="chat-message ai-message">
                  <img
                    src={response.img}
                    alt="Chat response"
                    className="chat-response-image"
                  />
                </div>
              )}
              {response?.navigationSection && (
                <div className="chat-action-buttons">
                  <button
                    className="navigate-button"
                    onClick={handleButtonClick(handleNavigate)}
                  >
                    Go to{" "}
                    {response.navigationSection.charAt(0).toUpperCase() +
                      response.navigationSection.slice(1)}
                  </button>
                </div>
              )}
              {response?.connectMessage && (
                <div className="chat-action-buttons">
                  <button
                    className="connect-button"
                    onClick={handleButtonClick(handleConnect)}
                  >
                    Connect to representative
                  </button>
                </div>
              )}
              {response?.showWhatsApp && (
                <div className="chat-action-buttons">
                  <button
                    className="whatsapp-button"
                    onClick={handleButtonClick(handleWhatsAppClick)}
                  >
                    Chat on WhatsApp
                  </button>
                </div>
              )}
              {response?.actions &&
                Array.isArray(response.actions) &&
                response.actions.length > 0 && (
                  <div className="chat-action-buttons chat-actions-horizontal">
                    {response.actions.map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        className="action-button"
                        onClick={handleButtonClick(() => {
                          // Special handling for URL with display_text="Live talk"
                          if (
                            response?.url?.url &&
                            response?.url?.display_text === "Live talk"
                          ) {
                            // Navigate to Explore tab with URL in state
                            navigate("/explore", {
                              state: { url: response.url.url },
                            });
                            // Exit chat mode
                            setIsVisible(false);
                            setResponse(null);
                            setIsFocused(false);
                            setIsChatBarDisabled(false);
                            setMessageHistory([]);
                          } else {
                            // Default behavior: call handleActionClick
                            handleActionClick(action.cmd);
                          }
                        })}
                      >
                        {action.title || action.cmd || "Action"}
                      </button>
                    ))}
                  </div>
                )}
              {response?.cards &&
                Array.isArray(response.cards) &&
                response.cards.length > 0 && (
                  <div
                    className="chat-cards-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {response.cards.map((card, cardIndex) => (
                      <div
                        key={cardIndex}
                        className="chat-card"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="card-close-button"
                          onClick={handleButtonClick(
                            handleCloseCard(cardIndex),
                          )}
                        >
                          ×
                        </button>
                        {card.img && (
                          <img
                            src={card.img}
                            alt={card.title || "Card"}
                            className="card-image"
                          />
                        )}
                        {card.title && (
                          <div className="card-title">{card.title}</div>
                        )}
                        {card.text && (
                          <div className="card-text">{card.text}</div>
                        )}
                        {card.actions &&
                          Array.isArray(card.actions) &&
                          card.actions.length > 0 && (
                            <div className="card-actions">
                              {card.actions.map((action, actionIndex) => (
                                <button
                                  key={actionIndex}
                                  className="card-action-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (action.url) {
                                      // Check if URL starts with map.abuzz.tech
                                      if (
                                        action.url.startsWith(
                                          "https://map.abuzz.tech/",
                                        )
                                      ) {
                                        // Navigate to Navigate tab with URL in state
                                        navigate("/navigate", {
                                          state: { url: action.url },
                                        });
                                        // Close the card and chat messages view
                                        handleCloseCard(cardIndex)(e);
                                        setMessageHistory([]); // Clear message history to close the chat window
                                        setIsVisible(false);
                                        setResponse(null);
                                        setIsFocused(false);
                                        setIsChatBarDisabled(false);
                                      } else {
                                        // Default behavior: open in new window
                                        window.open(action.url, "_blank");
                                      }
                                    }
                                  }}
                                >
                                  {action.title || action.text || "Action"}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Chat input bar */}
        {isChatBarVisible && (
          <div
            className={`chat-input-container ${isFocused ? "focused" : ""} ${
              isChatBarDisabled ? "disabled" : ""
            }`}
            onClick={isChatBarDisabled ? undefined : handleChatAreaClick}
          >
            {showVoiceActionTooltip && (
              <div
                className="voice-action-tooltip"
                role="dialog"
                aria-label="Voice actions help"
              >
                <button
                  type="button"
                  className="voice-action-tooltip-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissVoiceActionTooltip();
                  }}
                  aria-label="Close voice actions help"
                  title="Close"
                >
                  x
                </button>
                <p className="voice-action-tooltip-title">
                  Voice action shortcuts
                </p>
                <div className="voice-action-tooltip-list">
                  <div className="voice-action-tooltip-item">
                    <span className="voice-action-tooltip-icon">
                      <MicIcon />
                    </span>
                    <span>
                      Tap <b>once</b> to send voice note.
                    </span>
                  </div>
                  <div className="voice-action-tooltip-item">
                    <span className="voice-action-tooltip-icon">
                      <ChevronIcon />
                    </span>
                    <span>
                      Tap + <b>hold + slide up</b> to:
                    </span>
                  </div>
                  <div className="voice-action-tooltip-item voice-action-tooltip-item-sub">
                    <span className="voice-action-tooltip-icon">
                      <LockIcon />
                    </span>
                    <span>Continue recording by locking the mic.</span>
                  </div>
                  <div className="voice-action-tooltip-item voice-action-tooltip-item-sub">
                    <span className="voice-action-tooltip-icon">
                      <TalkIcon />
                    </span>
                    <span>Start a live conversation instantly.</span>
                  </div>
                  <div className="voice-action-tooltip-item voice-action-tooltip-item-sub">
                    <span className="voice-action-tooltip-icon">
                      <CallIcon />
                    </span>
                    <span>Get on a call over the phone.</span>
                  </div>
                  <div className="voice-action-tooltip-item voice-action-tooltip-item-sub">
                    <span className="voice-action-tooltip-icon">
                      <WhatsAppIcon />
                    </span>
                    <span>Switch to WhatsApp.</span>
                  </div>
                </div>
              </div>
            )}
            <div className="chat-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                name="chat_message"
                id="chat_message"
                autoComplete="new-password"
                inputMode="text"
                enterKeyHint="send"
                value={message}
                onChange={
                  isChatBarDisabled
                    ? undefined
                    : (e) => setMessage(e.target.value)
                }
                onKeyPress={isChatBarDisabled ? undefined : handleKeyPress}
                onFocus={isChatBarDisabled ? undefined : handleInputFocus}
                onClick={
                  isChatBarDisabled ? undefined : dismissVoiceActionTooltip
                }
                placeholder="Ask me anything..."
                className="chat-input"
                disabled={isLoading || isChatBarDisabled}
              />
              <div
                onClick={(e) => {
                  if (isChatBarDisabled) return;
                  dismissVoiceActionTooltip();
                  if (suppressLockedSendClickRef.current) {
                    suppressLockedSendClickRef.current = false;
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  // Mic mode (empty message, not locked): swallow click so input never gets focused.
                  if (!message.trim() && !isLockedVoiceRecording) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  if (isLockedVoiceRecording) {
                    handleButtonClick(handleSendLockedVoice)(e);
                    return;
                  }
                  handleButtonClick(handleSend)(e);
                }}
                onContextMenu={(e) => {
                  // Android Chrome long-press can emit a context menu; suppress it for mic hold.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragStart={(e) => {
                  e.preventDefault();
                }}
                onPointerDown={(e) => {
                  dismissVoiceActionTooltip();
                  suppressLockedSendClickRef.current = false;
                  if (!message.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  // Outside fullscreen input mode, mic press should not focus input.
                  if (!message.trim() && !isFocused) {
                    blurInputForMicInteraction();
                  }
                  handleVoiceGestureStart(e);
                }}
                onMouseDown={(e) => {
                  dismissVoiceActionTooltip();
                  if (!message.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  if (!message.trim() && !isFocused) {
                    blurInputForMicInteraction();
                  }
                }}
                tabIndex={-1}
                className={`send-button ${
                  isLoading || isChatBarDisabled ? "disabled" : ""
                } ${isVoiceGestureActive ? "recording" : ""} ${
                  isMicRecording ? "mic-live" : ""
                } ${isLockedVoiceRecording ? "locked-send" : ""}`}
              >
                <span className="send-icon">
                  {isLoading && !isSendingTextMessage ? (
                    <img
                      src="/looc.png"
                      alt="Loading"
                      className="loading-spinner"
                    />
                  ) : isLockedVoiceRecording ? (
                    "➤"
                  ) : isVoiceGestureActive ? (
                    <img
                      src="/looc.png"
                      alt="Recording"
                      className="loading-spinner"
                    />
                  ) : message.trim() ? (
                    "➤"
                  ) : (
                    <MicIcon />
                  )}
                </span>
              </div>
            </div>
            {(isLockedVoiceRecording || isVoiceGestureActive) && (
              <div
                className={`voice-gesture-shell ${voiceGesture.mode === "cancel" ? "cancel-ready" : ""}`}
              >
                <div
                  className={`voice-cancel-track ${isLockedVoiceRecording ? "locked" : ""}`}
                  style={{ "--cancel-progress": cancelProgress }}
                >
                  <div className="voice-recording-status">
                    {isLockedVoiceRecording && (
                      <button
                        type="button"
                        className="voice-recording-trash-button"
                        onClick={handleDiscardLockedVoice}
                        aria-label="Discard recording"
                        title="Discard recording"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 400 400"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="voice-recording-trash"
                        >
                          <path
                            fill="#ffffff"
                            d="M 158.5,37.5 C 187.169,37.3334 215.835,37.5 244.5,38C 247.321,39.8224 249.821,41.9891 252,44.5C 254.039,48.7453 256.206,52.912 258.5,57C 285.167,57.3333 311.833,57.6667 338.5,58C 341.937,59.1049 344.437,61.2716 346,64.5C 346.667,74.1667 346.667,83.8333 346,93.5C 344.437,96.7284 341.937,98.8951 338.5,100C 247.167,100.667 155.833,100.667 64.5,100C 61.0629,98.8951 58.5629,96.7284 57,93.5C 56.3333,83.8333 56.3333,74.1667 57,64.5C 58.5629,61.2716 61.0629,59.1049 64.5,58C 91.1667,57.6667 117.833,57.3333 144.5,57C 146.794,52.912 148.961,48.7453 151,44.5C 153.37,41.9614 155.87,39.6281 158.5,37.5 Z"
                          />
                          <path
                            fill="#ffffff"
                            d="M 76.5,119.5 C 159.833,119.5 243.167,119.5 326.5,119.5C 321.786,190.141 317.286,260.807 313,331.5C 313.897,349.899 305.731,362.066 288.5,368C 230.5,368.667 172.5,368.667 114.5,368C 97.2692,362.066 89.1025,349.899 90,331.5C 85.7142,260.808 81.2142,190.141 76.5,119.5 Z"
                          />
                        </svg>
                      </button>
                    )}
                    <span className="voice-recording-dot" />
                    <span className="voice-recording-timer">
                      {formatRecordingTimer(recordingElapsedMs)}
                    </span>
                  </div>
                  <span className="voice-cancel-label">
                    {isLockedVoiceRecording
                      ? "Recording locked"
                      : voiceGesture.mode === "cancel"
                        ? "Release to cancel"
                        : "< Slide left to cancel"}
                  </span>
                </div>
                <div
                  className={`voice-action-rail ${
                    isVoiceGestureActive
                      ? "active"
                      : isLockedVoiceRecording
                        ? "closing"
                        : ""
                  }`}
                >
                  <div className="voice-action-items">
                    <div className="voice-action-chevron">
                      <span className="voice-action-icon">
                        <ChevronIcon />
                      </span>
                    </div>
                    {VOICE_ACTIONS.map((action) => {
                      const IconComponent = action.icon;
                      return (
                        <div
                          key={action.id}
                          className={`voice-action-item ${
                            voiceGesture.selectedAction === action.id &&
                            voiceGesture.mode !== "cancel"
                              ? "selected"
                              : ""
                          }`}
                        >
                          <span className="voice-action-icon">
                            <IconComponent />
                          </span>
                          <span className="voice-action-label">
                            {action.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {micError && <div className="voice-action-error">{micError}</div>}
          </div>
        )}
      </div>
    </>
  );
};

export default ChatInterface;
